import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getInvoices, deleteInvoice, updateInvoice, getInvoiceUsage } from '../redux/slices/invoiceSlice';
import { generateInvoicePdf, markPdfDownloaded } from '../utils/generateInvoicePdf';
import { getWatermarkText } from '../redux/slices/settingsSlice';
import UsageMeter from '../components/UsageMeter';
import { Plus, Trash2, Edit, Eye, Download, CheckCircle, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const Invoices = () => {
  const dispatch = useDispatch();
  const { invoices, isLoading, usage } = useSelector((state) => state.invoice);
  const { subscription } = useSelector((state) => state.subscription);
  const { user } = useSelector((state) => state.auth);
  const { watermarkText } = useSelector((state) => state.settings);
  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';
  const [filter, setFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    dispatch(getInvoices());
    dispatch(getInvoiceUsage());
    dispatch(getWatermarkText());
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      await dispatch(deleteInvoice(id));
      toast.success('Invoice deleted successfully');
    }
  };

  const handleExportPDF = async (invoice) => {
    if (subscription?.plan_id?.features?.pdfExport === false) {
      toast.error('PDF export is not available in your current plan. Upgrade to access this feature.');
      return;
    }

    try {
      await generateInvoicePdf(invoice, {
        customBranding: !!subscription?.plan_id?.features?.customBranding,
        apiUrl: API_URL,
      });
      markPdfDownloaded(user?._id || user?.id);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleQuickStatusUpdate = async (invoiceId, newStatus) => {
    // Show confirmation for important status changes
    if (newStatus === 'paid' || newStatus === 'cancelled') {
      const confirmMessage = newStatus === 'paid' 
        ? 'Are you sure you want to mark this invoice as paid?'
        : 'Are you sure you want to cancel this invoice?';
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      await dispatch(updateInvoice({ 
        id: invoiceId, 
        data: { 
          status: newStatus,
          ...(newStatus === 'paid' && { paidDate: new Date().toISOString() })
        } 
      })).unwrap();
      
      const statusMessages = {
        paid: 'Invoice marked as paid',
        sent: 'Invoice marked as sent',
        draft: 'Invoice marked as draft',
        overdue: 'Invoice marked as overdue',
        cancelled: 'Invoice marked as cancelled'
      };
      
      toast.success(statusMessages[newStatus] || 'Status updated successfully');
    } catch (error) {
      toast.error(error || 'Failed to update invoice status');
    }
  };

  const filteredInvoices = filter === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === filter);

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800',
      sent: 'px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800',
      paid: 'px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800',
      overdue: 'px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800',
      cancelled: 'px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800',
    };
    return badges[status] || 'px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Manage your invoices</p>
        </div>
        {usage?.atCap ? (
          <Link to="/subscriptions" className="btn btn-primary">
            Upgrade
          </Link>
        ) : (
          <Link to="/invoices/new" className="btn btn-primary flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Create Invoice</span>
          </Link>
        )}
      </div>

      <UsageMeter usage={usage} compact />

      {/* Filters */}
      <div className="flex space-x-2 overflow-x-auto">
        {['all', 'draft', 'sent', 'paid', 'overdue'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              filter === status
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      <div className="card">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredInvoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Invoice #</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Client</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Issue Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Due Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice._id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{invoice.invoiceNumber}</td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{invoice.client_info.name}</p>
                        <p className="text-sm text-gray-500">{invoice.client_info.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-medium">
                      ${invoice.total.toLocaleString()} {invoice.currency}
                    </td>
                    <td className="py-3 px-4">
                      <span className={getStatusBadge(invoice.status)}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {format(new Date(invoice.issueDate), 'MMM dd, yyyy')}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {invoice.dueDate ? format(new Date(invoice.dueDate), 'MMM dd, yyyy') : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Quick Status Update - Mark as Paid */}
                        {invoice.status !== 'paid' && (
                          <button
                            onClick={() => handleQuickStatusUpdate(invoice._id, 'paid')}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Mark as Paid"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Status Dropdown */}
                        <div className="relative dropdown-container">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === invoice._id ? null : invoice._id)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                            title="Change Status"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          
                          {openDropdown === invoice._id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                              <div className="py-1">
                                {['draft', 'sent', 'paid', 'overdue', 'cancelled'].map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => {
                                      handleQuickStatusUpdate(invoice._id, status);
                                      setOpenDropdown(null);
                                    }}
                                    disabled={invoice.status === status}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                                      invoice.status === status 
                                        ? 'text-gray-400 cursor-not-allowed' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <Link
                          to={`/invoices/edit/${invoice._id}`}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleExportPDF(invoice)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Export PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(invoice._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">No invoices found</p>
            <Link to="/invoices/new" className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2 inline-block">
              Create your first invoice
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;

